import { useEffect, useState } from 'react'

import { apiGetCheckPoints } from '@src/api/point.api'
import { ChainId, NATIVE_TOKEN_ADDRESS, isTestnet } from '@src/configs/chainInfo'
import { MIN_MILES_FOR_MON_PURCHASE, MONAD_TESTNET_WMON_ADDRESS } from '@src/constants/miles'
import { IExchangeData } from '@src/types/exchange'
import { createContextLogger, isFeatureEnabled } from '@src/utils'

const logger = createContextLogger('useMilesRequirement')

interface UseMilesRequirementProps {
    buyData: IExchangeData
    sellData: IExchangeData
    mainWalletAddress: string | undefined
}

// Rename interface
export interface MilesRequirementStatus {
    hasEnoughMiles: boolean
    currentMiles: number
    isLoadingMiles: boolean
    error: string | null
    requiresMiles: boolean
}

// Rename hook
export const useMilesRequirement = ({ buyData, sellData, mainWalletAddress }: UseMilesRequirementProps) => {
    const [milesStatus, setMilesStatus] = useState<MilesRequirementStatus>({
        hasEnoughMiles: false,
        currentMiles: 0,
        isLoadingMiles: false,
        error: null,
        requiresMiles: false,
    })

    const isMonadTestnetToken = (data: IExchangeData) => {
        return data.token?.chainId === ChainId.MonadTestnet
    }

    const isMegaEthToken = (data: IExchangeData) => {
        return data.token?.chainId === ChainId.MegaEthTestnet
    }

    const checkRequiresMiles = () => {
        if (!isFeatureEnabled('MILES_REQUIREMENT') || !isTestnet(buyData.token?.chainId)) {
            return false
        }

        if (!isMonadTestnetToken(buyData) && !isMegaEthToken(buyData)) {
            return false
        }

        return true
    }

    const fetchMiles = async (address: string) => {
        setMilesStatus(prev => ({ ...prev, isLoadingMiles: true, error: null }))
        try {
            const { points: miles } = await apiGetCheckPoints(address)
            setMilesStatus({
                hasEnoughMiles: miles >= MIN_MILES_FOR_MON_PURCHASE,
                currentMiles: miles,
                isLoadingMiles: false,
                error: null,
                requiresMiles: checkRequiresMiles(),
            })
        } catch (error) {
            logger.error('Failed to fetch miles:', error)
            setMilesStatus(prev => ({
                ...prev,
                isLoadingMiles: false,
                error: 'Failed to fetch miles',
                requiresMiles: checkRequiresMiles(),
            }))
        }
    }

    useEffect(() => {
        const requiresMiles = checkRequiresMiles()

        if (requiresMiles && mainWalletAddress) {
            fetchMiles(mainWalletAddress)
        } else {
            setMilesStatus(prev => ({
                ...prev,
                requiresMiles,
                isLoadingMiles: false,
                error: null,
            }))
        }
    }, [buyData.token?.address, sellData.token?.address, mainWalletAddress])

    return milesStatus
}
